/**
 * Listing-URL construction + Danish-character transliteration.
 *
 * Extracted from helper.ts so this logic is dependency-free and therefore
 * unit-testable (helper.ts pulls in the font stylesheet and other build-only
 * modules that cannot load in a plain test runner). helper.ts re-exports these
 * symbols, so every existing `import { getCaseUrl } from "@/utils/helper"` keeps working.
 */

/**
 * Transliterate Danish letters to ASCII digraphs so generated URL slugs are
 * pure ASCII.
 *
 * Why this exists: a slug containing raw æ/ø/å bytes is published verbatim into
 * the sitemap `<loc>` entries and into `<a href>` links. The Next.js routing
 * layer rejects requests whose path contains those raw non-ASCII bytes with
 * HTTP 400, so every listing with æ/ø/å in its address became un-crawlable and
 * was deindexed by Google. Transliterating to ae/oe/aa keeps URLs clean ASCII
 * with no percent-encoding required.
 *
 *   æ → ae   ø → oe   å → aa   (and the uppercase forms)
 */
export function transliterateDanish(input: string): string {
  return input
    .replace(/Æ/g, "Ae")
    .replace(/æ/g, "ae")
    .replace(/Ø/g, "Oe")
    .replace(/ø/g, "oe")
    .replace(/Å/g, "Aa")
    .replace(/å/g, "aa");
}

export function getCaseUrl(
  property: {
    advertisementAddressOrDisplayName: string;
    zipAndCity: string;
    id: string;
  },
  includeEjendom = true,
) {
  let url = property.advertisementAddressOrDisplayName;
  if (url.indexOf(property.zipAndCity) === -1) {
    url = `${url}-${property.zipAndCity}`;
  }
  // Transliterate Danish letters to ASCII first, then replace any remaining
  // non-word characters with a dash. After transliteration the slug is pure
  // ASCII — identical to the previous output for ASCII-only addresses — so the
  // URL needs no percent-encoding and the route never returns HTTP 400.
  url = `${transliterateDanish(url)
    .toLowerCase()
    .replace(/[^\w-]/g, "-")}____${property.id}`;
  if (!includeEjendom) {
    return url;
  }

  return `/ejendom/${url}`;
}

/**
 * Backwards compatibility for old listing URLs.
 *
 * The canonical URL is now the transliterated ASCII slug, but old links still
 * exist in the wild — most importantly the percent-encoded æ/ø/å form
 * (`…strandg%C3%A5rdsvej…`) that browsers produced and that users bookmarked or
 * Google indexed. Because a listing is resolved by the trailing `____<uuid>`,
 * the slug portion is purely cosmetic: any slug that reaches the page resolves
 * to the right case, so we can safely redirect every non-canonical variant to
 * the canonical ASCII URL.
 *
 * @param requestedSlug the `[caseId]` route segment as received (may be
 *   percent-encoded, raw, or already canonical)
 * @param property the resolved case
 * @returns the absolute path to 308-redirect to, or `null` if the request is
 *   already canonical (serve as-is)
 */
export function canonicalCaseRedirect(
  requestedSlug: string,
  property: {
    advertisementAddressOrDisplayName: string;
    zipAndCity: string;
    id: string;
  },
): string | null {
  const canonicalSlug = getCaseUrl(property, false);

  let requested = requestedSlug;
  try {
    // Old links arrive percent-encoded; decode so they compare against the
    // plain ASCII canonical. Tolerate malformed encoding rather than throwing.
    requested = decodeURIComponent(requestedSlug);
  } catch {
    /* keep the raw value */
  }

  return requested === canonicalSlug ? null : `/ejendom/${canonicalSlug}`;
}
